import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Leaf, Mail, Smartphone, ArrowLeft, Camera, Sparkles, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { GoogleAuthButton } from '@/components/auth/GoogleAuthButton';
import { EmailAuthForm } from '@/components/auth/EmailAuthForm';
import { PhoneAuthForm } from '@/components/auth/PhoneAuthForm';
import { trackEvent } from '@/lib/gtag';
import { captureUtmFromLocation, getAnalyticsContext } from '@/lib/utm';

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'login' ? 'login' : 'signup';
  const [mode, setMode] = useState<'signup' | 'login'>(initialMode);

  const isSignup = mode === 'signup';

  useEffect(() => {
    // Persist any UTMs that arrived via query (from landing redirect) and fire page view
    captureUtmFromLocation();
    trackEvent('signup_page_view', { ...getAnalyticsContext(), mode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSuccess = () => {
    const redirect = searchParams.get('redirect') || '/';
    navigate(redirect);
  };

  const toggleMode = () => {
    const next = isSignup ? 'login' : 'signup';
    setMode(next);
    const params = new URLSearchParams(searchParams);
    params.set('mode', next);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col safe-area-pt safe-area-pb text-[17px] leading-relaxed">
      <header className="h-14 flex items-center px-4 border-b border-border">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/')}
          className="h-11 w-11 -ml-2"
          aria-label="Voltar para a página inicial"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
      </header>

      <div className="flex-1 flex items-center justify-center p-4 leaf-pattern">
        <div className="w-full max-w-md animate-grow">
          <div className="text-center mb-4">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full gradient-nature mb-3">
              <Leaf className="h-7 w-7 text-primary-foreground" />
            </div>
            <h1 className="text-2xl font-bold text-foreground font-display">VerdeIA</h1>
            <p className="text-base text-muted-foreground mt-1">
              Identificação e cuidados de plantas
            </p>
          </div>

          {isSignup && (
            <div className="mb-4 grid grid-cols-3 gap-2">
              {[
                { icon: Camera, label: 'Foto identifica' },
                { icon: Sparkles, label: 'Cuidados IA' },
                { icon: ShieldCheck, label: 'Sem cartão' },
              ].map((b, i) => (
                <div key={i} className="flex flex-col items-center gap-1 p-2 rounded-lg bg-card/70 border border-border/60 text-center">
                  <b.icon className="h-5 w-5 text-primary" aria-hidden />
                  <span className="text-xs leading-tight text-foreground">{b.label}</span>
                </div>
              ))}
            </div>
          )}

          <Card className="glass-card border-0 shadow-lg">
            <CardHeader className="text-center pb-2 px-4 pt-5">
              <CardTitle className="text-xl">
                {isSignup ? 'Crie sua conta grátis' : 'Entrar na sua conta'}
              </CardTitle>
              <CardDescription className="text-base">
                {isSignup
                  ? '3 identificações grátis por mês · Sem cartão'
                  : 'Bem-vindo de volta à VerdeIA'}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4 px-4 pb-5">
              <GoogleAuthButton />

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">ou</span>
                </div>
              </div>

              <Tabs defaultValue="email" className="w-full">
                <TabsList className="grid w-full grid-cols-2 h-12">
                  <TabsTrigger value="email" className="flex items-center gap-2 h-10 text-base">
                    <Mail className="h-4 w-4" aria-hidden />
                    E-mail
                  </TabsTrigger>
                  <TabsTrigger value="phone" className="flex items-center gap-2 h-10 text-base">
                    <Smartphone className="h-4 w-4" aria-hidden />
                    SMS
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="email" className="mt-4">
                  <EmailAuthForm onSuccess={handleSuccess} mode={mode} onSwitchMode={toggleMode} />
                </TabsContent>

                <TabsContent value="phone" className="mt-4">
                  <PhoneAuthForm onSuccess={handleSuccess} mode={mode} />
                </TabsContent>
              </Tabs>

              <div className="pt-1 text-center">
                <button
                  type="button"
                  onClick={toggleMode}
                  className="text-base text-muted-foreground hover:text-primary transition-colors min-h-11 px-2"
                >
                  {isSignup ? (
                    <>Já tem conta? <span className="text-primary font-medium">Entrar</span></>
                  ) : (
                    <>Não tem conta? <span className="text-primary font-medium">Criar conta grátis</span></>
                  )}
                </button>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-4 px-4">
            Ao continuar, você concorda com nossos Termos de Serviço e Política de Privacidade.
          </p>
        </div>
      </div>
    </div>
  );
}
